import { useEffect, useState } from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    FormGroup,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { EncounterStatusEnum } from '../../../Enum/EncounterStatusEnum';
import { RootState } from '../../../reducers';
import { parseISO } from 'date-fns';
import { formatDate } from '../../../utils/dateHelpers';
import {
    getTop,
    setShowProcedureSignOffModal,
    updateEncounters,
    setClaimError,
    setDraftEncounterList,
    hasDuplicateDiagnosisCodes,
    setDateOfService,
    setPlaceOfServiceId,
} from '../../../store/procedureSignOffEncounter.slice';
import { largeModalStyle } from '../../../utils/constants';
import ProcedureSignOffEncounterEdit from './ProcedureSignOffEncounterEdit';
import { copyObject, debounce } from '../../../utils/common';
import { fetchMemberByName, setMembersListByName, setSearchName } from '../../../store/memberEncounterBillingReport.slice';
import { setSelectedMember } from '../../../store/memberDetail.slice';
import { fetchPlacesOfEncounter } from '../../../store/adminCPTCodesSlice.slice';

const ProcedureSignOffEncounter = (props) => {
    const dispatch = useAppDispatch();
    const { dateOfService, draftEncounterList, showProcedureSignOffModal, placeOfServiceId } = useSelector(
        (state: RootState) => state.procedureSignOffEncounter
    );
    const { membersListByName, searchName, searchLoading } = useSelector((state: RootState) => state.memberEncounterBillingReport);
    const { selectedMember } = useSelector((state: RootState) => state.memberDetail);
    const { placesOfEncounters } = useSelector((state: RootState) => state.adminCPTCodesSlice);
    const [showDxError, setShowDxError] = useState(false);
    const validateCpt = () => {
        if (draftEncounterList?.Encounters.some((e) => !Boolean(e.CptCodeId))) {
            dispatch(setClaimError({ Show: true, Message: 'Please add a primary CPT code to all encounters before submitting.' }));
            return false;
        }

        if (draftEncounterList?.Encounters.some((e) => !Boolean(e.Units))) {
            dispatch(setClaimError({ Show: true, Message: "Please verify you've selected Units." }));
            return false;
        }

        return true;
    };

    const handleOnSave = (isReview = false, sendToWaystar = false) => {
        let hasDuplicate = hasDuplicateDiagnosisCodes(draftEncounterList);
        if (hasDuplicate && !sendToWaystar) {
            dispatch(setClaimError({ Show: true, Message: 'Duplicate diagnosis found.' }));
            return;
        }

        if (!selectedMember.Gender || selectedMember.Gender.toString() === '0') {
            dispatch(setClaimError({ Show: true, Message: 'Please select a Male or Female gender for claim submission.' }));
            return;
        }

        if (draftEncounterList?.Encounters.length === 0) {
            dispatch(setClaimError({ Show: true, Message: 'No encounters to submit.' }));
            return;
        }

        for (var encounter of draftEncounterList?.Encounters) {
            if (encounter.DiagnosisList.length === 0 || !encounter.DiagnosisList[0].Diagnosis) {
                dispatch(setClaimError({ Show: true, Message: 'Please add at least one diagnosis to all encounters before submitting.' }));
                return;
            }

            if (encounter.PlaceOfEncounterValue === 0 || !encounter.PlaceOfEncounterId) {
                dispatch(setClaimError({ Show: true, Message: 'Please enter a place of service.' }));
                return;
            }
        }

        if (!validateCpt()) {
            return;
        }

        let isDiagnosisMissed = false;
        draftEncounterList.Encounters.forEach((encounter) => {
            encounter.DiagnosisList.forEach((diagnosis) => {
                if (Boolean(diagnosis.DiagnosisFirstList?.length) && !Boolean(diagnosis.FirstDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisSecondList?.length) && !Boolean(diagnosis.SecondDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisThirdList?.length) && !Boolean(diagnosis.ThirdDiagnosis)) {
                    isDiagnosisMissed = true;
                }
                if (Boolean(diagnosis.DiagnosisFourthList?.length) && !Boolean(diagnosis.FourthDiagnosis)) {
                    isDiagnosisMissed = true;
                }
            });
        });

        if (isDiagnosisMissed) {
            dispatch(setClaimError({ Show: true, Message: 'Please select a diagnosis' }));
            setShowDxError(true);
            return;
        }

        if (isReview) {
            dispatch(updateEncounters(draftEncounterList, EncounterStatusEnum.ForReview, null, false, false, sendToWaystar));
        } else {
            dispatch(updateEncounters(draftEncounterList, EncounterStatusEnum.ToSubmit, null, false, false, sendToWaystar));
        }
    };

    const handlePlaceOfService = (placeOfServiceId: string) => {
        const placeOfServiceItem = placesOfEncounters.find((pos) => pos.Id === placeOfServiceId) || null;
        // Use ?? not || so CMS place-of-service value 0 is not replaced with '' (falsy).
        dispatch(setPlaceOfServiceId(placeOfServiceId ?? ''));
        let data = copyObject(draftEncounterList);
        for (var encounter of data.Encounters) {
            encounter.PlaceOfEncounterValue = placeOfServiceItem?.Value ?? '';
            encounter.PlaceOfEncounterId = placeOfServiceId;
        }
        dispatch(setDraftEncounterList(data));
    };

    useEffect(() => {
        dispatch(fetchPlacesOfEncounter());
    }, [dispatch]);

    useEffect(() => {
        if (!showProcedureSignOffModal) {
            dispatch(setMembersListByName([]));
            dispatch(setSearchName(''));
        }
    }, [showProcedureSignOffModal, dispatch]);

    const renderBody = () => {
        return (
            <>
                <Stack spacing={2}>
                    <Grid container>
                        <Grid size={8}>
                            <Paper elevation={3} style={{ padding: '8px', textAlign: 'left' }}>
                                <Divider style={{ marginBottom: '16px' }}>
                                    <Chip label="Member" />
                                </Divider>
                                <Stack direction="row" spacing={2}>
                                    <Stack spacing={2}>
                                        <Avatar
                                            alt={selectedMember?.FirstName + ' ' + selectedMember?.LastName}
                                            src={selectedMember?.ProfileImageUrl}
                                            sx={{ width: 144, height: 144 }}
                                            className="profile-image"
                                        />
                                    </Stack>
                                    <Stack style={{ width: '100%' }} spacing={2}>
                                        <FormGroup>
                                            <Stack direction="row" spacing={2}>
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="First Name"
                                                    variant="outlined"
                                                    value={selectedMember ? selectedMember.FirstName : ''}
                                                />
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="Middle Name"
                                                    variant="outlined"
                                                    value={selectedMember && selectedMember.MiddleName ? selectedMember.MiddleName : ''}
                                                />
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="Last Name"
                                                    variant="outlined"
                                                    value={selectedMember ? selectedMember.LastName : ''}
                                                />
                                            </Stack>
                                        </FormGroup>
                                        <FormGroup>
                                            <Stack direction="row" spacing={2}>
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="AHCCCS ID"
                                                    variant="outlined"
                                                    value={selectedMember ? selectedMember.Mem_AHCCCS_ID || '' : ''}
                                                />
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="DOB"
                                                    variant="outlined"
                                                    value={selectedMember ? new Date(selectedMember.DOB).toLocaleDateString() || '' : ''}
                                                />
                                                <FormControl variant="standard" fullWidth>
                                                    <InputLabel htmlFor="formatted-text-mask-input">SSN</InputLabel>
                                                    <Input
                                                        disabled={true}
                                                        value={
                                                            selectedMember && selectedMember.SSN
                                                                ? 'XXX-XX-' + selectedMember.SSN.substring(selectedMember.SSN.length - 4)
                                                                : ''
                                                        }
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </FormGroup>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid size={4}>
                            <Paper elevation={3} style={{ padding: '8px', marginLeft: '8px', textAlign: 'left', minHeight: '208px' }}>
                                <Divider style={{ marginBottom: '16px' }}>
                                    <Chip label="Encounter Information" />
                                </Divider>
                                <Stack direction="row" spacing={2}>
                                    <Stack style={{ width: '100%' }} spacing={2}>
                                        <FormGroup>
                                            <Stack spacing={2}>
                                                <TextField
                                                    aria-readonly="true"
                                                    fullWidth
                                                    label="Date of Service"
                                                    variant="outlined"
                                                    value={dateOfService ? formatDate(dateOfService, 'MM/dd/yyyy hh:mm a') : ''}
                                                />
                                            </Stack>
                                        </FormGroup>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={2}>
                        <FormControl style={{ minWidth: '200px' }}>
                            <InputLabel id="demo-simple-select-label">Place of Service</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Place of Service"
                                value={placeOfServiceId || ''}
                                onChange={(e) => {
                                    handlePlaceOfService(e.target.value as string);
                                }}
                            >
                                {placesOfEncounters.map(function (pos) {
                                    return (
                                        <MenuItem key={pos.Id} value={pos.Id}>
                                            {`${pos.Name} - ${pos.Value}`}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Divider />
                    <ProcedureSignOffEncounterEdit
                        dxError={showDxError}
                        isCreateClaim={props.isCreateClaim}
                        encounterStatus={props.encounterStatus}
                        searchClaims={props.searchClaims}
                    />
                    <Box>
                        <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                            <Button
                                className="button-120"
                                variant="contained"
                                onClick={() => {
                                    handleOnSave(true);
                                }}
                            >
                                Save for Review
                            </Button>
                            <Button
                                className="button-120"
                                variant="contained"
                                onClick={() => {
                                    handleOnSave(false);
                                }}
                            >
                                Submit Claim
                            </Button>
                            <Button
                                className="button-120"
                                variant="contained"
                                disabled={!props.isCreateClaim && props.encounterStatus === EncounterStatusEnum.ForReview}
                                onClick={() => {
                                    handleOnSave(false, true);
                                }}
                            >
                                Submit Claim & Send to Waystar
                            </Button>
                            <Button
                                className="button-120"
                                variant="outlined"
                                onClick={() => {
                                    dispatch(setShowProcedureSignOffModal(false));
                                }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </>
        );
    };

    return (
        <Modal
            open={showProcedureSignOffModal}
            style={{ overflow: 'scroll' }}
            onClose={() => {
                dispatch(setShowProcedureSignOffModal(false));
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={largeModalStyle} style={{ top: getTop(draftEncounterList) }}>
                <Stack spacing={2}>
                    <Typography variant="h5">{props.isCreateClaim ? 'Create Claim' : 'Edit Procedure Sign Off'}</Typography>
                    {props.isCreateClaim && (
                        <>
                            <Divider />
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <FormControl fullWidth>
                                        <TextField
                                            id="date-of-service-picker"
                                            type="datetime-local"
                                            label="Date Of Service"
                                            InputLabelProps={{ shrink: true }}
                                            value={dateOfService ? formatDate(dateOfService, "yyyy-MM-dd'T'HH:mm") : ''}
                                            onChange={(e) => {
                                                const date = e.target.value ? parseISO(e.target.value) : null;
                                                dispatch(setDateOfService(date));
                                            }}
                                            inputProps={{
                                                max: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm"),
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={6}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            id="member_name"
                                            getOptionLabel={(membersListByName) => `${membersListByName.LastName}, ${membersListByName.FirstName}`}
                                            options={membersListByName}
                                            isOptionEqualToValue={(option, value) => option.Id === value.Id}
                                            noOptionsText={'No Members Found'}
                                            filterOptions={(x) => x}
                                            loadingText={'Searching..'}
                                            loading={searchLoading}
                                            inputValue={searchName}
                                            onInputChange={(event, value) => {
                                                dispatch(setSearchName(value));
                                                // Clear the list when user starts typing a new search
                                                dispatch(setMembersListByName([]));
                                            }}
                                            onChange={(event, value: any) => {
                                                value !== null && dispatch(setSelectedMember(value));
                                            }}
                                            onKeyUp={() => {
                                                if (searchName.length >= 3) {
                                                    debounce(() => {
                                                        dispatch(fetchMemberByName(searchName));
                                                    }, 600)();
                                                }
                                            }}
                                            renderInput={(params) => <TextField {...params} label="Search by Member Name or AHCCCS ID" />}
                                            renderOption={(props, membersListByName) => (
                                                <Box component={'li'} {...props} key={membersListByName.Id}>
                                                    {membersListByName.LastName}, {membersListByName.FirstName} - {membersListByName.Mem_AHCCCS_ID} -{' '}
                                                    {membersListByName.StatusDescription}
                                                </Box>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Divider />
                        </>
                    )}
                    {renderBody()}
                </Stack>
            </Box>
        </Modal>
    );
};

export default ProcedureSignOffEncounter;
